import React from 'react'
import { Typography, Button, Box, Chip, Tooltip } from '@mui/material'
import AttachFileIcon from '@mui/icons-material/AttachFile'

import { feedback } from '../../core/feedback'
import { requests } from '../../core/ajax'
import { downloadFile } from '../../core/utils'
import { FileUploaderAgnostic } from '../file-uploader-agnostic'
import { UploadTooltip } from '../upload-tooltip'

/**
 * @typedef {object} FileValue
 * @property {string} name - Nombre del archivo.
 * @property {string} data - Contenido del archivo en formato base64 o URL.
 */

/**
 * @typedef {object} CurrentDoc
 * @property {string | number} id - ID del documento en el servidor.
 * @property {string} nombre - Nombre del documento sin extensión.
 * @property {string} extension - Extensión del archivo (ej: 'pdf', 'jpg').
 */

/**
 * @typedef {object} FileUploaderInputChangeEvent
 * @property {object} target - Un objeto que simula la estructura de un evento de cambio nativo.
 * @property {string} target.name - El nombre del campo, proveniente de la prop name.
 * @property {FileValue | null} target.value - El archivo seleccionado o null si se elimina.
 */

/**
 * @typedef {object} FileUploaderInputProps
 * @property {string} name - Nombre del campo para identificación en el evento onChange.
 * @property {FileValue} [value] - El archivo actual cargado localmente.
 * @property {string} [title] - Título o etiqueta que se muestra sobre el campo.
 * @property {boolean} [required] - Si es true, marca el campo como obligatorio con asterisco rojo.
 * @property {boolean} [readOnly=false] - Si es true, solo muestra el archivo actual sin permitir cambios.
 * @property {function(FileUploaderInputChangeEvent): void} onChange - Callback ejecutado cuando se carga o elimina un archivo.
 * @property {object} [fileProps] - Props adicionales para el FileUploaderAgnostic (maxSize, acceptedFormats, etc.).
 * @property {CurrentDoc} [currentDoc] - Documento actual almacenado en el servidor (si existe).
 */

/**
 * Un componente de carga de archivos con visualización y descarga.
 *
 * Proporciona una interfaz completa para:
 * - Seleccionar y cargar archivos locales
 * - Mostrar archivos cargados como chips con nombre
 * - Descargar archivos locales o desde el servidor
 * - Eliminar archivos seleccionados
 * - Tooltip informativo sobre formatos y tamaños permitidos
 * - Modo de solo lectura para visualización
 *
 * @component
 * @example
 * const MyForm = () => {
 *   const [archivo, setArchivo] = useState(null);
 *   const [docActual, setDocActual] = useState({ id: 123, nombre: 'contrato', extension: 'pdf' });
 *
 *   const handleChange = (e) => {
 *     console.log('Archivo seleccionado:', e.target.value);
 *     setArchivo(e.target.value);
 *   };
 *
 *   const fileProps = {
 *     maxSize: 5, // MB
 *     acceptedFormats: ['pdf', 'jpg', 'png']
 *   };
 *
 *   return (
 *     <FileUploaderInput
 *       name="documento"
 *       title="Documento de identidad"
 *       value={archivo}
 *       onChange={handleChange}
 *       fileProps={fileProps}
 *       currentDoc={docActual}
 *       required
 *     />
 *   );
 * };
 *
 * @param {FileUploaderInputProps} props - Las props del componente FileUploaderInput.
 */
export const FileUploaderInput = (props) => {
  const { name, value, title, onChange, fileProps, readOnly = false, hidden = false } = props
  if (hidden) return null
  const { getById } = requests

  const handleFileUploadError = (data) => feedback('error', data.mssg)

  const handleDownloadCloudFile = (id) => {
    getById('/documento', id)
      .then(({ data }) => {
        downloadFile(data['documento'], data['extension'], data['nombre'])
      })
      .catch(() => {
        console.debug('Download document fail')
        feedback('error', 'Error al descargar el documento')
      })
  }

  const handleDownloadLocalFile = ({ data, name }) => {
    const link = document.createElement('a')
    link.target = '_blank'
    link.href = data
    link.download = name
    link.click()
  }

  const handleDeleteFile = () => {
    const inputElement = document.getElementById(`file-upload-${name}`)
    if (inputElement) {
      inputElement.value = ''
    }
    onChange({ target: { name, value: null } })
  }

  return (
    <>
      {title ? (
        <Box display="flex" flexDirection="horizontal">
          <Typography style={{ marginRight: '0.5em' }} variant="subtitle1" gutterBottom>
            {title}
            {props.required ? <span style={{ color: '#B3272C' }}> *</span> : null}
          </Typography>
          {!readOnly ? <UploadTooltip file={fileProps} /> : null}
        </Box>
      ) : null}
      {!readOnly ? (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          height={48}
          flexWrap="wrap"
        >
          <FileUploaderAgnostic
            name={name}
            onSuccess={onChange}
            onError={handleFileUploadError}
            required={Boolean(props.required && !props.value && !props.currentDoc)}
            {...fileProps}
          >
            {({ loading, fileName, disabled }) => (
              <Button component="span" variant="outlined">
                Seleccionar archivo
              </Button>
            )}
          </FileUploaderAgnostic>
        </Box>
      ) : null}
      {value ? (
        <Chip
          size="small"
          avatar={<AttachFileIcon />}
          label={
            <Typography
              variant="body2"
              style={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 250 }}
            >
              {value.name}
            </Typography>
          }
          onClick={() => handleDownloadLocalFile(value)}
          onDelete={handleDeleteFile}
        />
      ) : props.currentDoc ? (
        <Tooltip
          arrow
          title={
            <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
              Descargar fichero: {props.currentDoc.nombre + '.' + props.currentDoc.extension}
            </Typography>
          }
        >
          <Chip
            size="small"
            avatar={<AttachFileIcon />}
            label={
              <Typography
                variant="body2"
                style={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 250 }}
              >
                {props.currentDoc.nombre + '.' + props.currentDoc.extension}
              </Typography>
            }
            onClick={() => handleDownloadCloudFile(props.currentDoc.id)}
          />
        </Tooltip>
      ) : readOnly ? (
        '--'
      ) : null}
    </>
  )
}
